/**
 * TODO: Delete when real backend is implemented
 *
 * Mocks `GET /cost/task` and `GET /cost/user`, plus the activity behind their charts.
 *
 * One task has one history. A contributor row is one pass from it, so opening that task shows
 * the same person, role, hours, frames, annotations, and cost. Each pass is priced at
 * $4.75 an hour, so cost per frame moves with the hours worked.
 *
 * Three different people staff a task group. Each task gives each of them one role, and a
 * second pass of that role (uncommon) stays with the same person.
 */
import dayjs from 'dayjs';
import { TaskType } from 'packages/lib/api/annotations/contract/enums';
import {
  CostRole,
  CostTaskItem,
  CostTaskResponse,
  CostUserItem,
  CostUserQuery,
  CostUserResponse,
} from 'packages/lib/api/annotations-ops/contract';
import {
  addDay,
  createRandom,
  datesInRange,
  DayActivity,
  dayActivity,
  emptyDay,
  hasFrames,
  hours,
  MockTaskGroup,
  money,
  seedFrom,
  summaryFromActivity,
  taskDateFromToken,
  taskForToken,
  taskGroupForTask,
  taskGroups,
  tasksForDay,
} from 'packages/lib/api/annotations-ops/clients/cost/mock/mock-hierarchy';

// 2026-10-05 Used Cursor (Claude Opus 5.5 Medium) to add Contributor and Task views
// 2026-10-09 Used Cursor (Grok 4.7) to make Task Group, Task, and Contributor mocks share one phase history
// 2026-10-09 Used Cursor (Grok 4.7) to price each pass at $4.75 an hour so cost follows hours worked

interface Person {
  name: string;
  email: string;
}

const NAMED_CONTRIBUTORS: Person[] = [
  'Aarav Sharma',
  'Diya Patel',
  'Rohan Gupta',
  'Ananya Iyer',
  'Vikram Nair',
  'Priya Menon',
  'Arjun Reddy',
  'Kavya Das',
  'Ishaan Bose',
  'Meera Pillai',
].map((name) => ({
  name,
  email: `${name.toLowerCase().replace(' ', '.')}@imerit.net`,
}));

const FIRST_NAMES = [
  'Asha',
  'Dev',
  'Fatima',
  'Hari',
  'Indira',
  'Jamal',
  'Leela',
  'Nikhil',
  'Omar',
  'Padma',
  'Quinn',
  'Ravi',
  'Sana',
  'Tara',
  'Uma',
  'Vivek',
  'Yusuf',
  'Zara',
] as const;

const LAST_NAMES = [
  'Banerjee',
  'Chaudhary',
  'Deshmukh',
  'Fernandes',
  'Ghosh',
  'Kapoor',
  'Krishnan',
  'Malhotra',
  'Naidu',
  'Okafor',
  'Qureshi',
  'Rao',
  'Singh',
  'Thomas',
  'Varma',
  'Wong',
] as const;

type Random = () => number;

const people: Person[] = [];

/** Named contributors first, then generated names, so every task group can have its own crew. */
const personAt = (index: number): Person => {
  const existing = people[index];
  if (existing) return existing;
  const named = NAMED_CONTRIBUTORS[index];
  if (named) {
    people[index] = named;
    return named;
  }

  const generated = index - NAMED_CONTRIBUTORS.length;
  const first = FIRST_NAMES[generated % FIRST_NAMES.length];
  const last =
    LAST_NAMES[Math.floor(generated / FIRST_NAMES.length) % LAST_NAMES.length];
  const cycle = Math.floor(
    generated / (FIRST_NAMES.length * LAST_NAMES.length)
  );
  const person = {
    name: cycle === 0 ? `${first} ${last}` : `${first} ${last} ${cycle + 1}`,
    email: `${first}.${last}.${index}@imerit.net`.toLowerCase(),
  };
  people[index] = person;
  return person;
};

const ROLES: readonly CostRole[] = ['Labeling', 'QA', 'Audit'];

/** iMerit rate for a pass. Cost per frame is this times hours, divided by the task's frames. */
const HOURLY_RATE = 4.75;

/**
 * How often a role is done a second time. The second pass is the same person; a third
 * would be the same person too, but two is enough to show a repeated stage.
 */
const SECOND_PASS_CHANCE: Record<CostRole, number> = {
  Labeling: 0.35,
  QA: 0.2,
  Audit: 0.1,
};

let rosterReady = false;
const crewByGroup = new Map<string, readonly [Person, Person, Person]>();
const groupsByEmail = new Map<string, MockTaskGroup[]>();

/**
 * Each person works about two task groups. Three consecutive people staff a group; which
 * of them labels, reviews, or audits is chosen per task.
 */
const ensureRoster = (): void => {
  if (rosterReady) return;
  const groups = taskGroups();
  const poolSize = Math.max(
    NAMED_CONTRIBUTORS.length,
    Math.floor((groups.length * 3) / 2)
  );

  groups.forEach((group, groupIndex) => {
    const base = (groupIndex * 3) % poolSize;
    const crew = [0, 1, 2].map((offset) =>
      personAt((base + offset) % poolSize)
    ) as [Person, Person, Person];
    crewByGroup.set(group.token, crew);
    crew.forEach((person) => {
      const assigned = groupsByEmail.get(person.email) ?? [];
      if (!assigned.includes(group)) assigned.push(group);
      groupsByEmail.set(person.email, assigned);
    });
  });
  rosterReady = true;
};

/** Group index and task index are the first and third fields of a hierarchy task token. */
const TASK_INDEX_PATTERN = /^([0-9a-f]{8})-[0-9a-f]{4}-4([0-9a-f]{3})-/i;

/** One person per role. The assignment shifts with the task, and stays fixed across passes. */
const holdersForTask = (
  crew: readonly [Person, Person, Person],
  taskToken: string
): Record<CostRole, Person> => {
  const match = TASK_INDEX_PATTERN.exec(taskToken);
  const rotation = match
    ? (Number.parseInt(match[1], 16) + Number.parseInt(match[2], 16)) % 3
    : 0;
  return {
    Labeling: crew[rotation],
    QA: crew[(rotation + 1) % 3],
    Audit: crew[(rotation + 2) % 3],
  };
};

const standaloneCrew = (random: Random): Record<CostRole, Person> => {
  const picked: Person[] = [];
  while (picked.length < ROLES.length) {
    const person =
      NAMED_CONTRIBUTORS[Math.floor(random() * NAMED_CONTRIBUTORS.length)];
    if (!picked.includes(person)) picked.push(person);
  }
  return { Labeling: picked[0], QA: picked[1], Audit: picked[2] };
};

/** A second pass only when the role's cost can be split into two positive amounts. */
const passCount = (role: CostRole, cost: number, random: Random): number => {
  if (cost <= 0) return 0;
  return cost >= 0.5 && random() < SECOND_PASS_CHANCE[role] ? 2 : 1;
};

// ---------------------------------------------------------------------------
// Task
// ---------------------------------------------------------------------------

interface MockTaskPhase {
  /** YYYY-MM-DD the pass was completed. */
  date: string;
  /** The whole task's industry cost. It cannot be split across passes. */
  industryCost: number;
  item: CostTaskItem;
}

interface TaskHistory {
  phases: MockTaskPhase[];
  numFrames: number | null;
  numAnnotations: number;
  industryCost: number;
}

const historyCache = new Map<string, TaskHistory>();

/**
 * Labeling, then QA, then Audit. Each role is one person, and a repeated pass stays with
 * that person. Each pass costs its hours times $4.75.
 */
const taskHistory = (taskToken: string): TaskHistory => {
  const cached = historyCache.get(taskToken);
  if (cached) return cached;

  ensureRoster();
  const record = taskForToken(taskToken);
  const group = taskGroupForTask(taskToken);
  const random = createRandom(seedFrom(`task:${taskToken}`));
  const crew = group ? crewByGroup.get(group.token) : undefined;
  const holders = crew
    ? holdersForTask(crew, taskToken)
    : standaloneCrew(random);

  const numFrames = record
    ? group && hasFrames(group.taskType)
      ? record.numFrames
      : null
    : random() < 0.75
    ? 1 + Math.floor(random() * 6)
    : null;
  const numAnnotations = record
    ? record.numAnnotations
    : 2 + Math.floor(random() * 40);
  const costs: Record<CostRole, number> = record
    ? {
        Labeling: record.labelingCost,
        QA: record.qaCost,
        Audit: record.auditCost,
      }
    : {
        Labeling: money(5 + random() * 40),
        QA: money(2 + random() * 15),
        Audit: random() < 0.5 ? money(1 + random() * 10) : 0,
      };
  const industryCost = record
    ? record.scaleCost
    : money(
        costs.Labeling *
          (random() < 0.5 ? 0.55 + random() * 0.4 : 1.1 + random() * 1)
      );

  const start =
    taskDateFromToken(taskToken) ??
    dayjs()
      .subtract(5 + Math.floor(random() * 50), 'day')
      .format('YYYY-MM-DD');
  let cursor = dayjs(start);
  const phases: MockTaskPhase[] = [];

  ROLES.forEach((role) => {
    const count = passCount(role, costs[role], random);
    if (count === 0) return;
    const holder = holders[role];
    for (let pass = 0; pass < count; pass += 1) {
      const hoursWorked = hours(0.3 + random() * 2.5);
      phases.push({
        date: cursor.format('YYYY-MM-DD'),
        industryCost,
        item: {
          contributor: holder.name,
          userEmail: holder.email,
          taskPhase: role,
          hoursWorked,
          numFrames,
          numAnnotations,
          totalCost: money(hoursWorked * HOURLY_RATE),
        },
      });
      cursor = cursor.add(1 + Math.floor(random() * 2), 'day');
    }
  });

  const history = { phases, numFrames, numAnnotations, industryCost };
  historyCache.set(taskToken, history);
  return history;
};

/** Adds one pass's spend and hours to the role it belongs to. */
const addRoleWork = (
  totals: DayActivity,
  role: CostRole,
  cost: number,
  hoursWorked: number
): void => {
  switch (role) {
    case 'Labeling':
      totals.labelingCost += cost;
      totals.labelingHours += hoursWorked;
      return;
    case 'QA':
      totals.numQATasks += 1;
      totals.qaCost += cost;
      totals.qaHours += hoursWorked;
      return;
    case 'Audit':
      totals.numAuditTasks += 1;
      totals.auditCost += cost;
      totals.auditHours += hoursWorked;
  }
};

const sumActivity = (days: DayActivity[]): DayActivity => {
  const totals = emptyDay();
  days.forEach((day) => addDay(totals, day));
  return totals;
};

/** The task's activity on each day one of its passes was completed. */
export const taskActivityByDate = (
  taskToken: string
): { date: string; totals: DayActivity }[] => {
  const { phases, numFrames, numAnnotations, industryCost } =
    taskHistory(taskToken);
  const byDate = new Map<string, DayActivity>();
  let countedTask = false;

  phases.forEach(({ date, item }) => {
    const totals = byDate.get(date) ?? emptyDay();
    addRoleWork(totals, item.taskPhase, item.totalCost, item.hoursWorked);
    // Frames, industry cost, and the task itself belong to the task, not to each pass.
    if (item.taskPhase === 'Labeling' && !countedTask) {
      totals.scaleCost += industryCost;
      totals.numTasks = 1;
      totals.numFrames = numFrames ?? 0;
      totals.numAnnotations = numAnnotations;
      countedTask = true;
    }
    byDate.set(date, totals);
  });

  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, totals]) => ({ date, totals }));
};

/** Mirrors `GET /cost/task`. */
export const buildMockTaskCost = (taskToken: string): CostTaskResponse => ({
  summary: summaryFromActivity(
    sumActivity(taskActivityByDate(taskToken).map(({ totals }) => totals))
  ),
  data: taskHistory(taskToken).phases.map(({ item }) => item),
});

// ---------------------------------------------------------------------------
// User
// ---------------------------------------------------------------------------

/** The task types of the task groups this person works. */
export const userTaskTypes = (userEmail: string): TaskType[] => {
  ensureRoster();
  const taskTypes = new Set<TaskType>();
  (groupsByEmail.get(userEmail) ?? []).forEach((group) =>
    taskTypes.add(group.taskType)
  );
  return [...taskTypes].sort();
};

interface UserPhase {
  date: string;
  item: CostUserItem;
}

const PHASE_LOOKBACK_DAYS = 14;
const phaseCache = new Map<string, UserPhase[]>();

/**
 * Passes this person completed in the range. Tasks are read from the groups they staff,
 * including ones that started shortly before the range whose review landed inside it.
 */
const userPhases = (query: CostUserQuery): UserPhase[] => {
  const key = `${query.userEmail}|${query.startDate}|${
    query.endDate
  }|${query.taskTypes.join(',')}`;
  const cached = phaseCache.get(key);
  if (cached) return cached;

  ensureRoster();
  const selected = new Set(query.taskTypes);
  const lookback = dayjs(query.startDate)
    .subtract(PHASE_LOOKBACK_DAYS, 'day')
    .format('YYYY-MM-DD');
  const phases: UserPhase[] = [];

  (groupsByEmail.get(query.userEmail) ?? []).forEach((group) => {
    if (selected.size > 0 && !selected.has(group.taskType)) return;
    datesInRange(lookback, query.endDate).forEach((day) => {
      const activity = dayActivity(group, day);
      if (activity.numTasks === 0) return;
      tasksForDay(group, day, activity).forEach((task) => {
        taskHistory(task.id).phases.forEach((phase) => {
          if (phase.item.userEmail !== query.userEmail) return;
          if (phase.date < query.startDate || phase.date > query.endDate)
            return;
          phases.push({
            date: phase.date,
            item: {
              taskToken: task.id,
              taskType: group.taskType,
              role: phase.item.taskPhase,
              hoursWorked: phase.item.hoursWorked,
              industryCost: phase.industryCost,
              numFrames: phase.item.numFrames,
              numAnnotations: phase.item.numAnnotations,
              totalCost: phase.item.totalCost,
            },
          });
        });
      });
    });
  });

  phases.sort(
    (a, b) =>
      a.date.localeCompare(b.date) ||
      a.item.taskToken.localeCompare(b.item.taskToken)
  );
  phaseCache.set(key, phases);
  return phases;
};

/**
 * Labeling passes carry the task counts, once per task, so a second labeling pass does
 * not double the task, its frames, or its industry cost.
 */
const userRowsActivity = (rows: CostUserItem[]): DayActivity => {
  const totals = emptyDay();
  const countedTasks = new Set<string>();
  rows.forEach((row) => {
    addRoleWork(totals, row.role, row.totalCost, row.hoursWorked);
    if (row.role !== 'Labeling' || countedTasks.has(row.taskToken)) return;
    countedTasks.add(row.taskToken);
    totals.numTasks += 1;
    totals.numFrames += row.numFrames ?? 0;
    totals.numAnnotations += row.numAnnotations;
    totals.scaleCost += row.industryCost;
  });
  return totals;
};

/** One point per day in the range, so the chart's axis matches the date picker. */
export const userActivityByDate = (
  query: CostUserQuery
): { date: string; totals: DayActivity }[] => {
  const phases = userPhases(query);
  return datesInRange(query.startDate, query.endDate).map(({ date }) => ({
    date,
    totals: userRowsActivity(
      phases.filter((phase) => phase.date === date).map((phase) => phase.item)
    ),
  }));
};

/** Mirrors `GET /cost/user`. */
export const buildMockUserCost = (query: CostUserQuery): CostUserResponse => {
  const data = userPhases(query).map((phase) => phase.item);
  return { summary: summaryFromActivity(userRowsActivity(data)), data };
};
